import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Calculator, 
  Building2,
  Plus, 
  Edit3, 
  Trash2, 
  Save,
  X,
  AlertCircle,
  CheckCircle,
  DollarSign,
  TrendingUp
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const COMCalculator = ({ calculationId, squareFootageLocations = [], onAssetsUpdate, onCalculationsUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("assets");

  const [comAssets, setComAssets] = useState([]);
  const [comCalculations, setComCalculations] = useState([]);

  const [showAssetForm, setShowAssetForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetForm, setAssetForm] = useState({
    asset_name: "",
    asset_type: "equipment",
    asset_category: "machinery",
    original_cost: "",
    accumulated_depreciation: "",
    net_book_value: "",
    acquisition_date: "",
    useful_life_years: "",
    depreciation_method: "straight_line",
    location_id: "none",
    dcaa_asset_class: "",
    com_eligible: true,
    com_notes: ""
  });

  const [comForm, setComForm] = useState({
    treasury_rate: "",
    rate_effective_date: "",
    dcaa_method: "net_book_value",
    dcaa_notes: ""
  });

  useEffect(() => {
    if (calculationId) {
      loadAssets();
      loadCalcs();
    }
  }, [calculationId]);

  const loadAssets = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/com-assets/${calculationId}`);
      const list = res.data.com_assets || [];
      setComAssets(list);
      onAssetsUpdate && onAssetsUpdate(list);
    } catch (e) {
      if (e.response?.status !== 404) setError(e.response?.data?.detail || 'Error loading COM assets');
    } finally { setLoading(false); }
  };

  const loadCalcs = async () => {
    try {
      const res = await axios.get(`${API}/com-calculations/${calculationId}`);
      const list = res.data.com_calculations || [];
      setComCalculations(list);
      onCalculationsUpdate && onCalculationsUpdate(list);
    } catch (e) {
      // tolerate 404
    }
  };

  const handleSaveAsset = async () => {
    setSaving(true); setError(null); setSuccess(null);
    if (!assetForm.asset_name.trim() || !assetForm.net_book_value) {
      setError('Asset name and Net Book Value are required'); setSaving(false); return;
    }
    try {
      const payload = {
        calculation_id: calculationId,
        asset_name: assetForm.asset_name,
        asset_type: assetForm.asset_type,
        asset_category: assetForm.asset_category,
        original_cost: parseFloat(assetForm.original_cost)||0,
        accumulated_depreciation: parseFloat(assetForm.accumulated_depreciation)||0,
        net_book_value: parseFloat(assetForm.net_book_value)||0,
        acquisition_date: assetForm.acquisition_date || null,
        useful_life_years: assetForm.useful_life_years ? parseInt(assetForm.useful_life_years) : null,
        depreciation_method: assetForm.depreciation_method,
        location_id: assetForm.location_id === 'none' ? null : assetForm.location_id,
        dcaa_asset_class: assetForm.dcaa_asset_class || null,
        com_eligible: !!assetForm.com_eligible,
        com_notes: assetForm.com_notes || null
      };
      let res;
      if (editingAsset && editingAsset._id) {
        res = await axios.put(`${API}/com-assets/${editingAsset._id}`, payload);
      } else {
        res = await axios.post(`${API}/com-assets`, payload);
      }
      if (res.data.success) {
        setSuccess(editingAsset ? 'Asset updated' : 'Asset created');
        setShowAssetForm(false); setEditingAsset(null);
        await loadAssets();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Error saving asset');
    } finally { setSaving(false); }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      const res = await axios.delete(`${API}/com-assets/${assetId}`);
      if (res.data.success) { setSuccess('Deleted'); loadAssets(); }
    } catch (e) { setError(e.response?.data?.detail || 'Error deleting asset'); }
  };

  const handleCalculateCOM = async () => {
    setCalculating(true); setError(null); setSuccess(null);
    if (!comForm.treasury_rate || parseFloat(comForm.treasury_rate) <= 0) { setError('Valid treasury rate required'); setCalculating(false); return; }
    if (!comForm.rate_effective_date) { setError('Rate effective date required'); setCalculating(false); return; }
    try {
      const payload = {
        treasury_rate: parseFloat(comForm.treasury_rate),
        rate_effective_date: comForm.rate_effective_date,
        dcaa_method: comForm.dcaa_method,
        dcaa_notes: comForm.dcaa_notes || null
      };
      const res = await axios.post(`${API}/calculate-com/${calculationId}`, payload);
      if (res.data.success) { setSuccess('COM calculated'); loadCalcs(); }
    } catch (e) { setError(e.response?.data?.detail || 'Error calculating COM'); }
    finally { setCalculating(false); }
  };

  if (loading) return (<div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"/><span className="ml-2">Loading COM...</span></div>);

  const assetSummary = {
    total: comAssets.length,
    eligible: comAssets.filter(a => a.com_eligible).length,
    totalNBV: comAssets.reduce((s,a)=> s + (a.net_book_value||0), 0)
  };

  return (
    <div className="space-y-6">
      {error && <Alert className="border-red-200 bg-red-50"><AlertCircle className="h-4 w-4"/><AlertDescription className="text-red-700">{error}</AlertDescription></Alert>}
      {success && <Alert className="border-green-200 bg-green-50"><CheckCircle className="h-4 w-4"/><AlertDescription className="text-green-700">{success}</AlertDescription></Alert>}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="assets" className="flex items-center gap-2"><Building2 className="h-4 w-4"/>Assets</TabsTrigger>
          <TabsTrigger value="calculator" className="flex items-center gap-2"><Calculator className="h-4 w-4"/>Calculator</TabsTrigger>
          <TabsTrigger value="results" className="flex items-center gap-2"><TrendingUp className="h-4 w-4"/>Results</TabsTrigger>
        </TabsList>

        <TabsContent value="assets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>COM Assets</span>
                <Button onClick={()=>{setShowAssetForm(true); setEditingAsset(null);}} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1"/>Add Asset</Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 p-4 rounded"><div className="text-sm text-blue-900">Total</div><div className="text-2xl font-bold text-blue-600">{assetSummary.total}</div></div>
                <div className="bg-green-50 p-4 rounded"><div className="text-sm text-green-900">Eligible</div><div className="text-2xl font-bold text-green-600">{assetSummary.eligible}</div></div>
                <div className="bg-blue-50 p-4 rounded"><div className="text-sm text-blue-900">Total NBV</div><div className="text-2xl font-bold text-blue-600">${assetSummary.totalNBV.toLocaleString()}</div></div>
              </div>

              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">NBV</TableHead>
                      <TableHead>Eligible</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comAssets.map(a => (
                      <TableRow key={a._id}>
                        <TableCell className="font-medium">{a.asset_name}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize text-xs">{a.asset_type}</Badge></TableCell>
                        <TableCell className="text-right">${Number(a.net_book_value||0).toLocaleString()}</TableCell>
                        <TableCell>{a.com_eligible ? 'Yes' : 'No'}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={()=>{setEditingAsset(a); setShowAssetForm(true);}}><Edit3 className="h-3 w-3 mr-1"/>Edit</Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={()=>handleDeleteAsset(a._id)}><Trash2 className="h-3 w-3"/></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {comAssets.length===0 && (<TableRow><TableCell colSpan={5} className="text-center text-slate-500">No assets yet</TableCell></TableRow>)}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {showAssetForm && (
            <Card>
              <CardHeader><CardTitle>{editingAsset? 'Edit Asset' : 'Add Asset'}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div><Label>Name</Label><Input value={assetForm.asset_name} onChange={e=>setAssetForm({...assetForm, asset_name:e.target.value})}/></div>
                  <div><Label>Type</Label><Input value={assetForm.asset_type} onChange={e=>setAssetForm({...assetForm, asset_type:e.target.value})}/></div>
                  <div><Label>Category</Label><Input value={assetForm.asset_category} onChange={e=>setAssetForm({...assetForm, asset_category:e.target.value})}/></div>
                  <div><Label>Original Cost</Label><Input type="number" value={assetForm.original_cost} onChange={e=>setAssetForm({...assetForm, original_cost:e.target.value})}/></div>
                  <div><Label>Accum. Depreciation</Label><Input type="number" value={assetForm.accumulated_depreciation} onChange={e=>setAssetForm({...assetForm, accumulated_depreciation:e.target.value})}/></div>
                  <div><Label>Net Book Value*</Label><Input type="number" value={assetForm.net_book_value} onChange={e=>setAssetForm({...assetForm, net_book_value:e.target.value})}/></div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={()=>{setShowAssetForm(false); setEditingAsset(null);}}><X className="h-4 w-4 mr-1"/>Cancel</Button>
                  <Button onClick={handleSaveAsset} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700"><Save className="h-4 w-4 mr-1"/>{saving? 'Saving...' : 'Save Asset'}</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="calculator">
          <Card>
            <CardHeader><CardTitle>COM Calculator</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div><Label>Treasury Rate (%)</Label><Input type="number" step="0.01" value={comForm.treasury_rate} onChange={e=>setComForm({...comForm, treasury_rate:e.target.value})}/></div>
                <div><Label>Rate Effective Date</Label><Input type="date" value={comForm.rate_effective_date} onChange={e=>setComForm({...comForm, rate_effective_date:e.target.value})}/></div>
                <div><Label>Method</Label><Input value={comForm.dcaa_method} onChange={e=>setComForm({...comForm, dcaa_method:e.target.value})}/></div>
              </div>
              <Button onClick={handleCalculateCOM} disabled={calculating || !comAssets.some(a=>a.com_eligible)} className="bg-green-600 hover:bg-green-700"><Calculator className="h-4 w-4 mr-1"/>{calculating? 'Calculating...' : 'Calculate'}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results">
          <Card>
            <CardHeader><CardTitle>COM Calculations</CardTitle></CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Total Eligible NBV</TableHead>
                      <TableHead className="text-right">Total COM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comCalculations.map((c)=>(
                      <TableRow key={c._id}>
                        <TableCell>{new Date(c.calculation_date||c.created_at||Date.now()).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{Number(c.com_rate||0).toFixed(2)}%</TableCell>
                        <TableCell className="text-right">${Number(c.total_eligible_nbv||0).toLocaleString()}</TableCell>
                        <TableCell className="text-right">${Number(c.total_com||0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                    {comCalculations.length===0 && (<TableRow><TableCell colSpan={4} className="text-center text-slate-500">No calculations yet</TableCell></TableRow>)}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default COMCalculator;